import './pbmPromotion.scss';

import { useEffect, useMemo, useState } from 'react';
import { MdDiscount } from 'react-icons/md';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';

import { useOrderContext } from '@/resources/hooks/useOrderContext';
import { useProductLink } from '@/resources/hooks/useProductLink';
import { useWebContext } from '@/resources/hooks/useWebContext';
import { getPbmDiscountCheck } from '@/routes/products';
import { ClientTypes } from '@/services/clients';
import { ICartProduct } from '@/store/ducks/order/types';
import { IAlert, IProduct } from '@/store/ducks/products/types';

import AskCpfModal from '../../askCpfModal/AskCpfModal';
import IdentificationModal from '../../identificationModal/IdentificationModal';
import { pbmBody, pbmHeaders } from './consts';
import { PbmPromotionsModal } from './PromotionsModal';

export type PbmPromotionType = 'Default' | 'Product' | 'Carousel' | 'OneLine';

interface PbmPromotionsLayoutProps {
  className?: string;
  type?: PbmPromotionType;
  pbmPromotions: IAlert[];
  product: IProduct | ICartProduct;
}

const MAIN_BUTTON_LABELS: Record<string, string> = {
  Product: 'Ativar Desconto',
  Applied: 'DESCONTO APLICADO',
  Default: 'Ver mais',
};

const MainButtonIcon = (type: PbmPromotionType) => {
  if (type === 'Product') return <MdDiscount />;
  return null;
};

export default function PbmPromotionsLayout({
  type = 'Default',
  pbmPromotions,
  product,
  className,
}: PbmPromotionsLayoutProps): JSX.Element {
  const { setFunction } = useOrderContext();
  const [showAskCpfModal, setShowAskCpfModal] = useState(false);
  const { order } = useWebContext();
  const color = useMemo(() => JSON.parse(window.configs).pbm_price_color, []);
  const history = useHistory();

  const { price } = product as IProduct;

  const hasAppliedPbm = order?.cart.products.some(
    (item) => item.id === product.id && item.price_type === 'pbm_price',
  );

  const isProductOrDefault = type === 'Product' ? 'Product' : 'Default';
  const [selectedPromotion, setSelectedPromotion] = useState<IAlert | undefined>(
    pbmPromotions?.sort(
      (a, b) => parseInt(a.discount_percentage ?? '0') - parseInt(b.discount_percentage ?? '0'),
    )[0],
  );
  const [showIdenfificationModal, setShowIdenfificationModal] = useState(false);
  const [showPbmPromotionsModal, setShowPbmPromotionsModal] = useState(false);
  const [promotions, setPromotions] = useState<IAlert[]>(pbmPromotions || []);

  const link = useProductLink({ product });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setFunction('setSelectedPromotion', setSelectedPromotion);
  }, [setSelectedPromotion]);

  const discountCheck = async () => {
    const response = await getPbmDiscountCheck({
      order_id: order?.id as number,
      full_product_id: product?.id as number,
      ean: product?.ean as string,
      quantity: selectedPromotion?.quantity as number,
    });

    if (response?.type === 'error') {
      toast(response.message, { type: response.type });
    }

    if (response?.type === 'limit-reached') {
      toast.warning(
        ' O desconto não está mais disponível para esse CPF, pois a quantidade limite de unidades foi atingida.',
      );
    }

    if (response?.type && response.type === 'no-cpf') {
      if (order?.client.type === ClientTypes.ANONYMOUS) {
        setShowIdenfificationModal(true);
      }

      if (order?.client.type === ClientTypes.DEFAULT && !order.client.cpf) {
        setShowAskCpfModal(true);
      }
    }

    if (response?.redirect_link) {
      window.open(response.redirect_link, '_blank');
    }

    if (
      response &&
      'prices' in response &&
      Array.isArray(response.prices) &&
      response.prices.length > 0 &&
      !response.prices.some(({ price }) => !price)
    ) {
      setPromotions(response.prices);
      setShowPbmPromotionsModal(true);
    }

    setLoading(false);
  };

  const handleMainButtonClick = async () => {
    setLoading(true);
    switch (type) {
      case 'Product':
        if (product?.id) await discountCheck();
        break;

      default:
        history.push(link);
        break;
    }
    setLoading(false);
  };

  const renderHeader = () => pbmHeaders(selectedPromotion, color)[isProductOrDefault]();
  const renderBody = () => (
    <div className="pbm-card-body" style={{ borderColor: color }}>
      <div
        className={`pbm-text-content flex-md-row ${type !== 'OneLine' ? 'flex-column' : ''} pt-2`}
      >
        {pbmBody(selectedPromotion, discountCheck, price)[isProductOrDefault]()}
      </div>
      <button
        className={`btn py-1 px-1 pbm-${type}-btn`}
        onClick={handleMainButtonClick}
        style={{ backgroundColor: hasAppliedPbm ? '#24963E' : color, color: 'white' }}
        disabled={loading}
      >
        {loading
          ? 'Carregando...'
          : MAIN_BUTTON_LABELS[hasAppliedPbm ? 'Applied' : isProductOrDefault]}{' '}
        {MainButtonIcon(type)}
      </button>
    </div>
  );

  const col = {
    default: 'col-xl-12 col-10',
    product: 'col-xl-7 col-12',
    noImage: 'col-xl-6 col-10',
  };

  const getCol = () => {
    if (isProductOrDefault === 'Default') return col.default;
    if (isProductOrDefault === 'Product' && selectedPromotion?.laboratory_image) return col.product;
    return col.noImage;
  };

  return (
    <div
      className={`pbm-card ${type} ${getCol()}
      col-md-10
      mb-0 ${className ?? ''}`}
    >
      {renderHeader()}
      {renderBody()}
      <IdentificationModal
        show={showIdenfificationModal}
        setShow={(state) => {
          setShowIdenfificationModal(state);
        }}
      />
      <AskCpfModal show={showAskCpfModal} setShow={setShowAskCpfModal} />
      <PbmPromotionsModal
        show={showPbmPromotionsModal}
        onHide={() => setShowPbmPromotionsModal(false)}
        promotions={promotions}
        product={product}
        color={color}
      />
    </div>
  );
}
